import { Box, Button, Card, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAtomValue } from 'jotai';
import { LocalShippingOutlined } from '@mui/icons-material';
import { RoutePaths } from '../../routes/AppRoutes';
import useOpenable from '../../hooks/useOpenable';
import { currentContextAtom } from '../../state';
import TopAppBar from '../../common/components/TopAppBar';
import { canEditDeliveryZonesAtom } from '../state';
import DeleteDeliveryZoneDialog from './DeleteDeliveryZoneDialog';
import DeliveryZoneDisplay from './DeliveryZoneDisplay';
import DeliveryZoneUpsertDialog from './DeliveryZoneUpsertDialog';

export enum CompanyPageSearchParams {
  OpenDialog = 'openDialog'
}

export default function CompanyDeliveryZones(): React.JSX.Element {
  const { t } = useTranslation(['common', 'settings']);
  const context = useAtomValue(currentContextAtom);
  const canEdit = useAtomValue(canEditDeliveryZonesAtom);
  const [searchParams] = useSearchParams();
  const { open, close, isOpen } = useOpenable();
  const [indexToDelete, setIndexToDelete] = useState<number | null>(null);

  const deleteZone = useCallback((index: number) => {
    setIndexToDelete(index);
  }, []);

  useEffect(() => {
    if (searchParams.has(CompanyPageSearchParams.OpenDialog)) {
      open();
    }
  }, [searchParams]);

  const numberOfDeliveryZoneString = useMemo((): string => {
    return context.deliveryZones?.length ? ` (${context.deliveryZones.length})` : '';
  }, [context.deliveryZones]);

  return (
    <>
      <TopAppBar
        configuration="small"
        backTo={RoutePaths.NAVIGATION_MAIN}
        shouldCheckHistory={false}
        title={`${t('settings:delivery_zones')}${numberOfDeliveryZoneString}`}
        sx={{
          position: 'fixed',
        }}
      />
      <Box
        sx={{
          height: {
            lg: 'calc(100% - 48px)',
          },
          overflow: {
            lg: 'auto',
          },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 0, lg: 3 },
          p: 2,
          mt: {
            xs: 5,
            lg: 0,
          },
          mb: {
            xs: 10,
            lg: 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            rowGap: 1,
            justifyContent: 'space-between',
            alignItems: {
              xs: 'start',
              sm: 'center',
            },
          }}
        >
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1, flex: 1 }}>
            <LocalShippingOutlined height={28} width={28} />
            <Typography variant="titleLarge">
              {t('settings:delivery_zones')} ({context.deliveryZones?.length})
            </Typography>
          </Box>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={open}
              sx={{
                position: {
                  xs: 'fixed',
                  lg: 'initial',
                },
                width: {
                  xs: 'calc(100% - 32px)',
                  lg: 'initial',
                },
                maxWidth: 320,
                bottom: 32,
                transform: {
                  xs: 'translate(-50%, 0)',
                  lg: 'unset',
                },
                left: '50%',
                zIndex: 10,
              }}
            >
              {t('settings:add_new_delivery_zone')}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexDirection: 'column', alignItems: { xs: 'center', lg: 'start' } }}>
          {context.deliveryZones?.length
            ? (
              <>
                {context.deliveryZones.map((deliveryZone, index, deliveryZones) => (
                  <DeliveryZoneDisplay
                    key={deliveryZone.id}
                    deliveryZone={deliveryZone}
                    index={index}
                    zoneListLength={deliveryZones.length}
                    deleteZone={canEdit ? () => deleteZone(index) : undefined}
                    readOnly={!canEdit}
                  />
                ))}
              </>
            ) : (
              <Card
                sx={{
                  backgroundColor: 'surfaceContainer.main',
                  borderRadius: 4,
                  textAlign: 'center',
                  p: 2,
                  my: 2,
                }}
              >
                <Typography variant="titleMedium">
                  {t('settings:no_delivery_zones_defined')}
                </Typography>
                <Typography variant="bodyMedium" color="onSurfaceVariant" sx={{ my: 1 }}>
                  {t('settings:no_delivery_zones_defined_description')}
                </Typography>
              </Card>
            )}
        </Box>
        {isOpen && <DeliveryZoneUpsertDialog open onClose={close} />}
        {indexToDelete !== null && (
          <DeleteDeliveryZoneDialog
            deliveryZone={context.deliveryZones![indexToDelete]}
            index={indexToDelete}
            numberOfZones={context.deliveryZones!.length}
            onClose={() => setIndexToDelete(null)}
          />
        )}
      </Box>
    </>
  );
}
